import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Edit3, CheckCircle2, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AgreementSign() {
  const { agId } = useParams();
  const nav = useNavigate();
  const [ag, setAg] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [mode, setMode] = useState("draw"); // draw | type
  const [typedSig, setTypedSig] = useState("");
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef([0, 0]);

  useEffect(() => {
    api.get(`/agreements/${agId}`).then((r) => {
      setAg(r.data);
      setSignerName(r.data.signer_name || "");
    }).catch(() => nav("/dashboard"));
  }, [agId, nav]);

  // canvas setup
  useEffect(() => {
    if (mode !== "draw" || !canvasRef.current) return;
    const c = canvasRef.current;
    c.width = c.offsetWidth * 2;
    c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [mode, ag]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return [x, y];
  };
  const start = (e) => { drawingRef.current = true; lastRef.current = getPos(e); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const [x, y] = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...lastRef.current);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = [x, y];
  };
  const end = () => { drawingRef.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const generateTypedSig = () => {
    const c = document.createElement("canvas");
    c.width = 600; c.height = 200;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0A0A0E";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#ffffff";
    ctx.font = "italic 56px 'Brush Script MT', cursive";
    ctx.fillText(typedSig, 20, 110);
    return c.toDataURL("image/png");
  };

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Type your name as legal signer"); return; }
    let signature_b64;
    if (mode === "draw") {
      const c = canvasRef.current;
      const ctx = c.getContext("2d");
      // Verify something was drawn (sample center pixel + a few)
      const data = ctx.getImageData(0, 0, c.width, c.height).data;
      let inked = false;
      for (let i = 3; i < data.length; i += 4 * 1000) { if (data[i] > 0) { inked = true; break; } }
      if (!inked) { toast.error("Please sign in the box"); return; }
      // Re-render with black bg so PDF embed reads correctly
      const out = document.createElement("canvas");
      out.width = c.width; out.height = c.height;
      const octx = out.getContext("2d");
      octx.fillStyle = "#0A0A0E";
      octx.fillRect(0, 0, out.width, out.height);
      octx.drawImage(c, 0, 0);
      signature_b64 = out.toDataURL("image/png");
    } else {
      if (!typedSig.trim()) { toast.error("Type your signature"); return; }
      signature_b64 = generateTypedSig();
    }
    setBusy(true);
    try {
      await api.post(`/agreements/${agId}/sign`, { signer_name: signerName, signature_b64 });
      toast.success("Signed.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign");
    } finally { setBusy(false); }
  };

  if (!ag) return <div className="max-w-[800px] mx-auto px-5 py-20 text-gray-400">Loading…</div>;

  if (ag.status === "signed") {
    return (
      <div className="max-w-[800px] mx-auto px-5 py-20 text-center" data-testid="agreement-already-signed">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-5 text-emerald-400" />
        <h2 className="font-display text-3xl mb-3">Already signed.</h2>
        <p className="text-gray-400 mb-6">This agreement was signed on {new Date(ag.signed_at).toLocaleString()}.</p>
        {ag.signed_pdf_path && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${ag.signed_pdf_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-[920px] mx-auto px-5 lg:px-10 py-12" data-testid="agreement-sign-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Back to dashboard</Link>
      <p className="label-mono mb-3">E-Signature</p>
      <h1 className="font-display text-3xl md:text-5xl font-light tracking-tighter mb-3">{ag.title}</h1>
      {ag.deal_summary && <p className="text-gray-400 mb-8">{ag.deal_summary}</p>}

      <div className="glass rounded-2xl p-7 mb-6 max-h-[400px] overflow-y-auto whitespace-pre-wrap text-sm text-gray-200 leading-relaxed" data-testid="agreement-body">
        {ag.body}
      </div>

      <div className="glass rounded-2xl p-7">
        <p className="label-mono mb-4">Sign below</p>
        <Input placeholder="Type your full legal name" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white mb-4" data-testid="sign-name" />

        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode("draw")} className={`flex-1 py-2 rounded-full text-xs border ${mode === "draw" ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid="sign-mode-draw">Draw</button>
          <button onClick={() => setMode("type")} className={`flex-1 py-2 rounded-full text-xs border ${mode === "type" ? "silver-gradient text-[#050507] border-transparent" : "border-white/10"}`} data-testid="sign-mode-type">Type</button>
        </div>

        {mode === "draw" ? (
          <div>
            <canvas
              ref={canvasRef}
              className="w-full h-40 bg-[#0A0A0E] border border-[#1A1A24] rounded-xl cursor-crosshair touch-none"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="sign-canvas"
            />
            <button onClick={clearCanvas} className="mt-2 text-xs text-gray-400 hover:text-white" data-testid="sign-clear">Clear</button>
          </div>
        ) : (
          <Input
            value={typedSig}
            onChange={(e) => setTypedSig(e.target.value)}
            placeholder="Your signature"
            className="bg-[#0A0A0E] border-[#1A1A24] h-16 rounded-xl text-white text-2xl italic font-serif"
            data-testid="sign-typed"
            style={{ fontFamily: "'Brush Script MT', cursive" }}
          />
        )}

        <button onClick={submit} disabled={busy} className="mt-5 w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50" data-testid="sign-submit">
          {busy ? "Signing…" : "Sign & Submit"}
        </button>
        <p className="text-[10px] text-gray-500 mt-3">Your signature, name, IP, and timestamp will be recorded on a PDF stored securely. This is legally binding under the U.S. ESIGN Act.</p>
      </div>
    </div>
  );
}
